import React, { useState, useCallback, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody } from '@mellify/ui';
import { PlatformIcon, platformLabels } from './PlatformIcon';
import type { SocialPlatform } from './PlatformIcon';
import { PermissionScopesModal, defaultPlatformPermissions } from './PermissionScopesModal';
import { useConnectPlatform } from '../hooks/useConnectPlatform';
import { ArrowRight } from 'lucide-react';
import { cn } from '@mellify/ui';
import { ConnectBlueskyModal } from './ConnectBlueskyModal';
import { ConnectDevtoModal } from './ConnectDevtoModal';
import { ConnectDailyDevModal } from './ConnectDailyDevModal';
import { ConnectMastodonModal } from './ConnectMastodonModal';
import { ConnectHashnodeModal } from './ConnectHashnodeModal';

interface ConnectChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PlatformOption {
  platform: SocialPlatform;
  description: string;
  comingSoon?: boolean;
  /** If true, uses a custom connection flow (not OAuth redirect) */
  directAuth?: boolean;
}

const platformOptions: PlatformOption[] = [
  {
    platform: 'facebook',
    description: 'Connect Facebook Pages to publish and engage',
  },
  {
    platform: 'instagram',
    description: 'Connect Instagram Business accounts',
  },
  {
    platform: 'x',
    description: 'Connect X accounts for posting and monitoring',
  },
  {
    platform: 'linkedin',
    description: 'Connect LinkedIn Pages and profiles',
  },
  {
    platform: 'youtube',
    description: 'Connect YouTube channels for video management',
  },
  {
    platform: 'bluesky',
    description: 'Connect Bluesky accounts for decentralized social',
    directAuth: true,
  },
  {
    platform: 'mastodon',
    description: 'Connect Mastodon accounts for federated social',
    directAuth: true,
  },
  {
    platform: 'reddit',
    description: 'Connect Reddit accounts for community engagement',
  },
  {
    platform: 'devto',
    description: 'Connect DEV Community for developer content',
    directAuth: true,
  },
  {
    platform: 'dailydev',
    description: 'Connect your daily.dev account to publish developer content',
    directAuth: true,
  },
  {
    platform: 'hashnode',
    description: 'Connect Hashnode to publish developer blog posts',
    directAuth: true,
  },
  {
    platform: 'tiktok',
    description: 'Connect TikTok Business accounts',
    comingSoon: true,
  },
  {
    platform: 'pinterest',
    description: 'Connect Pinterest Business accounts',
    comingSoon: true,
  },
];

// Memoized platform row to prevent re-renders of all rows on state changes
const PlatformRow = React.memo<{
  platform: SocialPlatform;
  description: string;
  comingSoon?: boolean;
  directAuth?: boolean;
  onSelect: (platform: SocialPlatform, directAuth?: boolean) => void;
}>(({ platform, description, comingSoon, directAuth, onSelect }) => {
  const handleClick = useCallback(() => {
    if (!comingSoon) {
      onSelect(platform, directAuth);
    }
  }, [platform, directAuth, comingSoon, onSelect]);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={comingSoon}
      className={cn(
        'flex items-center gap-4 w-full rounded-lg px-4 py-3 text-left transition-colors duration-150',
        comingSoon
          ? 'opacity-50 cursor-not-allowed bg-dark-50 dark:bg-dark-800/50'
          : 'hover:bg-dark-50 dark:hover:bg-dark-800 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
      )}
    >
      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-dark-700 border border-dark-200 dark:border-dark-600 shrink-0">
        <PlatformIcon platform={platform} size="md" animated={!comingSoon} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-dark-900 dark:text-dark-100">
            {platformLabels[platform]}
          </span>
          {comingSoon && (
            <span className="text-xs font-medium text-dark-400 dark:text-dark-500 bg-dark-100 dark:bg-dark-700 px-2 py-0.5 rounded-full">
              Coming Soon
            </span>
          )}
        </div>
        <p className="text-xs text-dark-500 dark:text-dark-400 truncate">
          {description}
        </p>
      </div>

      {!comingSoon && (
        <ArrowRight className="w-4 h-4 text-dark-400 dark:text-dark-500 shrink-0" />
      )}
    </button>
  );
});

PlatformRow.displayName = 'PlatformRow';

export const ConnectChannelModal: React.FC<ConnectChannelModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [selectedPlatform, setSelectedPlatform] = useState<SocialPlatform | null>(null);
  const [directAuthPlatform, setDirectAuthPlatform] = useState<SocialPlatform | null>(null);
  const { connect, isConnecting } = useConnectPlatform();

  const handlePlatformSelect = useCallback((platform: SocialPlatform, directAuth?: boolean) => {
    if (directAuth) {
      setDirectAuthPlatform(platform);
    } else {
      setSelectedPlatform(platform);
    }
  }, []);

  const handlePermissionClose = useCallback(() => {
    if (!isConnecting) {
      setSelectedPlatform(null);
    }
  }, [isConnecting]);

  const handleConnect = useCallback(async () => {
    if (!selectedPlatform) return;
    await connect(selectedPlatform);
  }, [selectedPlatform, connect]);

  const handleMainModalClose = useCallback(() => {
    if (!isConnecting) {
      setSelectedPlatform(null);
      setDirectAuthPlatform(null);
      onClose();
    }
  }, [isConnecting, onClose]);

  const handleDirectAuthClose = useCallback(() => {
    setDirectAuthPlatform(null);
  }, []);

  const handleDirectAuthSuccess = useCallback(() => {
    setDirectAuthPlatform(null);
    onClose();
  }, [onClose]);

  // Determine if the main modal should be visible
  const isMainModalVisible = isOpen && !selectedPlatform && !directAuthPlatform;

  // Render direct auth modals only when needed
  const directAuthModal = useMemo(() => {
    if (!directAuthPlatform) return null;

    switch (directAuthPlatform) {
      case 'bluesky':
        return (
          <ConnectBlueskyModal
            isOpen={true}
            onClose={handleDirectAuthClose}
            onSuccess={handleDirectAuthSuccess}
          />
        );
      case 'devto':
        return (
          <ConnectDevtoModal
            isOpen={true}
            onClose={handleDirectAuthClose}
            onSuccess={handleDirectAuthSuccess}
          />
        );
      case 'dailydev':
        return (
          <ConnectDailyDevModal
            isOpen={true}
            onClose={handleDirectAuthClose}
            onSuccess={handleDirectAuthSuccess}
          />
        );
      case 'mastodon':
        return (
          <ConnectMastodonModal
            isOpen={true}
            onClose={handleDirectAuthClose}
            onSuccess={handleDirectAuthSuccess}
          />
        );
      case 'hashnode':
        return (
          <ConnectHashnodeModal
            isOpen={true}
            onClose={handleDirectAuthClose}
            onSuccess={handleDirectAuthSuccess}
          />
        );
      default:
        return null;
    }
  }, [directAuthPlatform, handleDirectAuthClose, handleDirectAuthSuccess]);

  return (
    <>
      <Modal
        open={isMainModalVisible}
        onClose={handleMainModalClose}
        size="md"
        aria-labelledby="connect-channel-title"
      >
        <ModalHeader>
          <h2
            id="connect-channel-title"
            className="text-lg font-semibold text-dark-900 dark:text-dark-100"
          >
            Connect a Channel
          </h2>
        </ModalHeader>

        <ModalBody>
          <p className="text-sm text-dark-500 dark:text-dark-400 mb-5">
            Select a social media platform to connect to your workspace.
          </p>

          <div className="space-y-2">
            {platformOptions.map(({ platform, description, comingSoon, directAuth }) => (
              <PlatformRow
                key={platform}
                platform={platform}
                description={description}
                comingSoon={comingSoon}
                directAuth={directAuth}
                onSelect={handlePlatformSelect}
              />
            ))}
          </div>
        </ModalBody>
      </Modal>

      {/* Permission Scopes Modal (for OAuth-based platforms) */}
      {selectedPlatform && (
        <PermissionScopesModal
          isOpen={!!selectedPlatform}
          onClose={handlePermissionClose}
          onConnect={handleConnect}
          platform={selectedPlatform}
          permissions={defaultPlatformPermissions[selectedPlatform] || []}
          isConnecting={isConnecting}
        />
      )}

      {/* Direct auth modals - only rendered when needed */}
      {directAuthModal}
    </>
  );
};

ConnectChannelModal.displayName = 'ConnectChannelModal';